import Checkbox from '../Checkbox/Checkbox';
import './Textarea.scss';
import moment from 'moment';

/**
 * Uses the Tab key to indent instead of navigating
 * to the next form element.
 * @param {HTMLFormElement} textarea 
 */
const clickTabToIndent = (textarea) => {
  textarea.addEventListener('keydown', (keydownEvent) => {
    if (keydownEvent.key == 'Tab') {
      keydownEvent.preventDefault();
      const position = textarea.selectionStart;

      // set textarea value to: text before cursor + tab + text after cursor. The tab size is set in the css.
      textarea.value = textarea.value.substring(0, textarea.selectionStart) + "\t" + textarea.value.substring(textarea.selectionEnd);

      // put cursor at right position again.
      textarea.selectionEnd = position + 1;
    }
  });
}

/**
 * Replaces the code snippet with the desired code.
 * @param {string} codeSnippet 
 * @param {string} codeSnippetKey 
 * @param {number} cursorPosition 
 * @param {number} targetCursorPosition 
 */
const insertCodeSnippet = (textarea, codeSnippet, codeSnippetKey, cursorPosition, targetCursorPosition) => {
  const textAreaValueWithCodeSnippet = textarea.value.replace(codeSnippetKey, codeSnippet);
  textarea.value = textAreaValueWithCodeSnippet;
  textarea.selectionEnd = cursorPosition - targetCursorPosition;
}

/**
 * Creates a unique snippet based off the user
 * typing ';;' and the 1 or 2 characters preceding it.
 * @param {string} characterBeforeSemiColon1 
 * @param {string} characterBeforeSemiColon2 
 * @param {number} cursorPosition 
 */
const createCodeSnippet = (textarea, characterBeforeSemiColon1, characterBeforeSemiColon2, cursorPosition) => {
  let codeSnippet;
  let codeSnippetKey = `${characterBeforeSemiColon2};;`;
  let targetCursorPosition = 0;
  // ^^^ how many spaces to move the cursor into the snippet
  // start with 0 and see where it lands.
  // positive numbers move the cursor to the left.
  // negative to the right.

  // create headings (<h1>)
  if (!isNaN(characterBeforeSemiColon2) && parseInt(characterBeforeSemiColon2) < 7 && characterBeforeSemiColon1 === 'h') {
    codeSnippet = `<${characterBeforeSemiColon1}${characterBeforeSemiColon2}></${characterBeforeSemiColon1}${characterBeforeSemiColon2}>`;
    codeSnippetKey = `${characterBeforeSemiColon1}${characterBeforeSemiColon2};;`;
    targetCursorPosition = 0;
  }

  // create time: [12:21pm]
  else if (characterBeforeSemiColon2 === '/') {
    const time = moment().format("h:mma");
    codeSnippet = `[${time}] `;
    targetCursorPosition = -7;
  }

  // create <div>
  else if (characterBeforeSemiColon2 === 'd') {
    codeSnippet = `<div></div>`;
    targetCursorPosition = -2;
  }

  // create <a href="" target="_blank"></a>
  else if (characterBeforeSemiColon2 === 'a') {
    codeSnippet = `<a href="" target="_blank"></a>`;
    targetCursorPosition = -6;
  }

  // create <ul><li> or <ol><li>
  else if ((characterBeforeSemiColon1 === 'u' || characterBeforeSemiColon1 === 'o') && characterBeforeSemiColon2 === 'l') {
    codeSnippet =
      `<${characterBeforeSemiColon1}l>
  <li></li>
</${characterBeforeSemiColon1}l>`;
    codeSnippetKey = `${characterBeforeSemiColon1}${characterBeforeSemiColon2};;`;
    targetCursorPosition = -7;
  }

  // create code block
  else if (characterBeforeSemiColon1 === 'c' && characterBeforeSemiColon2 === 'b') {
    codeSnippet =
      `<pre>
  <code class="language-">
    
  </code>
</pre>`;
    codeSnippetKey = `${characterBeforeSemiColon1}${characterBeforeSemiColon2};;`;
    targetCursorPosition = -26;
  }

  // create inline code
  else if (characterBeforeSemiColon1 === 'c' && characterBeforeSemiColon2 === 'i') {
    codeSnippet = `<code></code>`;
    codeSnippetKey = `${characterBeforeSemiColon1}${characterBeforeSemiColon2};;`;
    targetCursorPosition = -2;
  }

  // create <li>
  else if (characterBeforeSemiColon1 === 'l' && characterBeforeSemiColon2 === 'i') {
    codeSnippet = `<li></li>`;
    codeSnippetKey = `${characterBeforeSemiColon1}${characterBeforeSemiColon2};;`;
    targetCursorPosition = 0;
  }

  // create <span>
  else if (characterBeforeSemiColon1 === 's' && characterBeforeSemiColon2 === 'p') {
    codeSnippet = `<span></span>`;
    codeSnippetKey = `${characterBeforeSemiColon1}${characterBeforeSemiColon2};;`;
    targetCursorPosition = -2;
  }

  // create <mark>
  else if (characterBeforeSemiColon1 !== 'e' && characterBeforeSemiColon2 === 'm') {
    codeSnippet = `<mark></mark>`;
    targetCursorPosition = -3;
  }

  // create <em></em>
  else if (characterBeforeSemiColon1 === 'e' && characterBeforeSemiColon2 === 'm') {
    codeSnippet = `<em></em>`;
    codeSnippetKey = `${characterBeforeSemiColon1}${characterBeforeSemiColon2};;`;
    targetCursorPosition = 0;
  }

  // create <br />
  else if (characterBeforeSemiColon1 === 'b' && characterBeforeSemiColon2 === 'r') {
    codeSnippet = `<br />`;
    codeSnippetKey = `${characterBeforeSemiColon1}${characterBeforeSemiColon2};;`;
    targetCursorPosition = -2;
  }

  // create class=""
  else if (characterBeforeSemiColon1 === 'c' && characterBeforeSemiColon2 === 'l') {
    codeSnippet = `class=""`;
    codeSnippetKey = `${characterBeforeSemiColon1}${characterBeforeSemiColon2};;`;
    targetCursorPosition = -3;
  }

  // create style=""
  else if (characterBeforeSemiColon1 === 's' && characterBeforeSemiColon2 === 't') {
    codeSnippet = `style=""`;
    codeSnippetKey = `${characterBeforeSemiColon1}${characterBeforeSemiColon2};;`;
    targetCursorPosition = -3;
  }

  // create <img />
  else if (characterBeforeSemiColon1 !== 'l' && characterBeforeSemiColon2 === 'i') {
    codeSnippet = `<img src="" alt="" />`;
    codeSnippetKey = `${characterBeforeSemiColon2};;`;
    targetCursorPosition = -7;
  }

  // create single letter element (<p>)
  else {
    codeSnippet = `<${characterBeforeSemiColon2.toLowerCase()}></${characterBeforeSemiColon2.toLowerCase()}>`;
  }

  insertCodeSnippet(textarea, codeSnippet, codeSnippetKey, cursorPosition, targetCursorPosition);
}

/**
 * Listens for a code snippet (p;;) to be entered and
 * replaces the snippet with code (`<p></p>`) and places
 * the cursor in the correct position (`<p>|</p>`).
 * @param {HTMLFormElement} textarea 
 */
const listenForCodeSnippets = (textarea) => {
  let typedCharacters = [];
  let previousTwoTypedCharacters;

  textarea.addEventListener('keyup', (keyupEvent) => {
    const typedCharacter = keyupEvent.key;
    const cursorPosition = textarea.selectionStart;
    typedCharacters.push(typedCharacter);
    previousTwoTypedCharacters = JSON.stringify(typedCharacters.slice(Math.max(typedCharacters.length - 2, 0)));

    if (previousTwoTypedCharacters === '[";",";"]') {
      const characterBeforeSemiColon1 = typedCharacters[typedCharacters.length - 4];
      const characterBeforeSemiColon2 = typedCharacters[typedCharacters.length - 3];
      createCodeSnippet(textarea, characterBeforeSemiColon1, characterBeforeSemiColon2, cursorPosition);
    }

  });
}

const focusHandler = (event) => {
  const textarea = event.target;
  clickTabToIndent(textarea);
  listenForCodeSnippets(textarea);
}

/**
 * Renders a textarea field and optional label wrapped in a form-group div.
 * 
 * Props: inputClassName, formGroupClassName, label, cols, defaultValue, disabled, errorText, isValid, id, name, onChange, placeholder, required, rows, checkbox, checkboxValue, checkboxLabel, onCheckboxChange, checkboxId
 */
const Textarea = (props) => {

  let inputClassName = `textarea`;
  if (props.inputClassName) {
    inputClassName += ` ${props.inputClassName}`;
  }

  let formGroupClassName = `form-group`;
  if (props.formGroupClassName) {
    formGroupClassName += ` ${props.formGroupClassName}`;
  }
  if (props.isValid === false) {
    formGroupClassName += ` form-group--invalid`;
  }

  return (
    <div className={formGroupClassName}>
      {props.label && <label className='textarea__label' htmlFor={props.id}>{props.label}</label>}
      <textarea
        className={inputClassName}
        cols={props.cols}
        disabled={props.disabled}
        id={props.id}
        name={props.name}
        onChange={props.onChange}
        placeholder={props.placeholder}
        required={props.required}
        rows={props.rows}
        value={props.value}
        defaultValue={props.defaultValue}
        onFocus={focusHandler}
      ></textarea>
      {props.errorText && <span className='error-text'>{props.errorText}</span>}
      {props.checkbox && 
        <Checkbox
          className='textarea__checkbox'
          id={props.checkboxId}
          label={props.checkboxLabel}
          defaultChecked={props.checkboxValue}
          onChange={props.onCheckboxChange}
        />
      }
    </div>
  )
};

export default Textarea;